import { useEffect, useCallback, useRef } from "react";
import { driver, DriveStep, Driver } from "driver.js";
import "driver.js/dist/driver.css";

export const useMealPlanTour = (
  shouldStart: boolean,
  onComplete: () => void
) => {
  const driverRef = useRef<Driver | null>(null);

  const startTour = useCallback(() => {
    // Detect if mobile (screen width < 768px, matching Mantine's 'sm' breakpoint)
    const isMobile = window.innerWidth < 768;

    const steps: DriveStep[] = [
      {
        element: '[data-tour="meal-plan-intro"]',
        popover: {
          title: "Your meal plan is ready!",
          description:
            "Welcome to your personalized meal plan. Let me show you how to customize recipes and get your grocery list.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="recipe-card-first"]',
        popover: {
          title: "Your recipes",
          description:
            "Each card shows a recipe for a specific day. Click on any card to view the full recipe details, ingredients, and instructions.",
          side: "right",
          align: "start",
        },
      },
      // The alternatives carousel is taught by the interactive first-swap
      // prompt after the tour (see MealPlanView), not by a passive slide.
      {
        element: '[data-tour="recipe-menu"]',
        popover: {
          title: "Customize your meals",
          description:
            "Don't care for a recipe? Click the menu button to substitute meals. You can request a new recommendation, customize with your own instructions, or choose from your saved recipes.",
          side: "left",
          align: "start",
        },
      },
      {
        element: '[data-tour="recipe-save"]',
        popover: {
          title: "Save to your cookbook",
          description:
            "Love a recipe? Click the heart to save it to your cookbook. You can access your saved recipes anytime and use them in future meal plans.",
          side: "left",
          align: "start",
        },
      },
      {
        element: isMobile
          ? '[data-tour="activate-button-mobile"]'
          : '[data-tour="activate-button-desktop"]',
        popover: {
          title: "Activate your plan",
          description:
            "When you're happy with your meal plan, click \"Activate\" to get your grocery list. Don't worry, you can still modify recipes after activating.",
          side: "bottom",
          align: "center",
        },
      },
    ];

    const driverObj = driver({
      showProgress: true,
      steps,
      onDestroyStarted: () => {
        driverObj.destroy();
        driverRef.current = null;
        onComplete();
      },
      popoverClass: "meal-plan-tour-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Got it!",
      progressText: "{{current}} of {{total}}",
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [onComplete]);

  useEffect(() => {
    if (shouldStart) {
      // Delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        startTour();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldStart, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour };
};
